// Conteúdo do guia "Motores para Robótica" — quizzes e referências externas.
// Mantém o componente GuiaMotores.js focado na estrutura, nas demos e no texto.

export const quizzes = {
  1: [
    {
      stem: "Um motor de brinquedo mostra na ficha: 6 V, 120 mA sem carga, 1,5 A em stall. Qual valor você usa para escolher o driver?",
      opts: [
        { t: "120 mA, porque é a corrente normal de funcionamento", ok: false, why: "É a corrente com o eixo livre, sem nada preso. Assim que o robô arranca ou encontra resistência, a corrente sobe muito acima disso. Um driver de 200 mA queimaria na primeira partida." },
        { t: "1,5 A, porque é o pior caso e acontece toda vez que o motor arranca", ok: true, why: "Correto. A corrente de stall é o máximo que o motor pode puxar e ocorre na partida e quando o eixo trava. Driver e bateria precisam suportar esse valor, com folga." },
        { t: "A média entre os dois, cerca de 800 mA", ok: false, why: "Não existe 'corrente média' que proteja você do pico. O driver precisa suportar o pior caso, mesmo que ele dure poucos milissegundos." },
        { t: "Nenhum: o driver é escolhido pela tensão, não pela corrente", ok: false, why: "A tensão precisa ser compatível, mas o que queima drivers é excesso de corrente. Os dois limites importam." },
      ],
    },
    {
      stem: "Por que um motor não deve ser ligado direto em um pino do Arduino?",
      opts: [
        { t: "Porque o pino fornece só uns 20–40 mA e o motor pede centenas de mA, além de devolver picos de tensão ao desligar", ok: true, why: "Correto. Corrente insuficiente (o motor não gira ou o pino queima) e o chute indutivo da bobina são os dois problemas. Por isso existe o driver, com sua fonte separada." },
        { t: "Porque o Arduino trabalha em 5 V e motores só funcionam em 12 V", ok: false, why: "Há muitos motores de 3 a 6 V. O problema não é a tensão, é a corrente que o pino consegue entregar e a proteção contra picos." },
        { t: "Porque o pino só sabe gerar sinal digital e o motor precisa de sinal analógico", ok: false, why: "Motores DC funcionam muito bem com sinal digital chaveado (PWM). O sinal não é o problema." },
        { t: "Pode ser ligado direto, desde que o motor seja pequeno", ok: false, why: "Mesmo o menor motor de vibração de celular pede mais do que o pino aguenta com segurança. Use sempre um transistor ou driver." },
      ],
    },
    {
      stem: "Você precisa que uma garra abra e feche em um ângulo controlado, cerca de 90°. Qual tipo de motor é o mais direto para isso?",
      opts: [
        { t: "Motor DC com redução", ok: false, why: "Ele gira continuamente e não sabe onde está. Você precisaria adicionar sensor e lógica de controle para parar no ângulo certo." },
        { t: "Servo", ok: true, why: "Correto. O servo já tem sensor de ângulo, redução e driver dentro. Basta um fio de sinal e dizer 'vá para 90°'." },
        { t: "Brushless", ok: false, why: "É um motor de velocidade, não de posição, e exige um ESC. Totalmente inadequado para uma garra." },
        { t: "Motor de passo", ok: false, why: "Funciona, mas é exagero: precisa de driver externo, ajuste de corrente e um fim de curso para saber onde é o zero. O servo resolve com um fio." },
      ],
    },
    {
      stem: "O que acontece se você esquecer de ligar o GND do Arduino ao GND do driver do motor (cada um com sua própria fonte)?",
      opts: [
        { t: "Nada, cada circuito tem sua fonte e funciona sozinho", ok: false, why: "O driver precisa interpretar o sinal do Arduino, e um sinal de 5 V só faz sentido em relação a um 0 V comum. Sem essa referência, o driver não sabe o que o Arduino está dizendo." },
        { t: "O motor gira ao contrário", ok: false, why: "O sentido depende das entradas IN1/IN2 e da ligação do motor, não do GND. Sem GND comum, o mais provável é o motor não responder ou responder de forma errática." },
        { t: "O sinal de controle fica sem referência e o driver não obedece (ou se comporta de forma imprevisível)", ok: true, why: "Correto. Tensão é sempre uma diferença entre dois pontos. O GND comum é o ponto de referência que faz o 'HIGH' do Arduino ser um 'HIGH' também para o driver." },
        { t: "O Arduino queima imediatamente", ok: false, why: "Em geral não queima, mas também não funciona. Quando funciona 'às vezes', é porque encontrou um caminho de GND por outro fio (USB, por exemplo), o que é ruim e imprevisível." },
      ],
    },
  ],
  2: [
    {
      stem: "O motor TT tem redução de 1:48. O que isso significa?",
      opts: [
        { t: "A roda gira 48 vezes mais rápido que o motor interno", ok: false, why: "É o contrário. Redução reduz a velocidade. O motor interno gira 48 voltas para a roda dar uma." },
        { t: "O motor interno gira 48 voltas para a roda dar uma volta, e o torque na roda fica cerca de 48 vezes maior", ok: true, why: "Correto. A caixa de redução divide a velocidade e multiplica o torque pelo mesmo fator (menos as perdas nas engrenagens)." },
        { t: "O motor tem 48 dentes na engrenagem", ok: false, why: "1:48 é a relação total entre a entrada e a saída, resultado de várias engrenagens em cascata, não o número de dentes de uma peça." },
        { t: "O motor consome 48 vezes menos corrente", ok: false, why: "A redução não altera o consumo elétrico do motor de forma direta; ela troca velocidade por torque no lado mecânico." },
      ],
    },
    {
      stem: "Um motor de 6 V é alimentado com PWM em 40 % de duty cycle. Que tensão média ele 'sente'?",
      opts: [
        { t: "6 V, porque o pico do pulso é sempre 6 V", ok: false, why: "O pico é 6 V, mas o motor tem inércia e indutância e não acompanha os pulsos. Ele responde à média, que é menor." },
        { t: "2,4 V", ok: true, why: "Correto: 6 V × 0,40 = 2,4 V. No Arduino, isso corresponde a analogWrite de aproximadamente 102 (40 % de 255)." },
        { t: "0 V, porque 40 % é abaixo do mínimo para girar", ok: false, why: "A tensão média é 2,4 V. É verdade que muitos motores com redução não vencem o atrito em duty baixo, mas isso é um limite mecânico, não elétrico. A pergunta é sobre a tensão." },
        { t: "3,6 V", ok: false, why: "Isso seria 60 % de duty. Você inverteu a conta: 40 % ligado significa 60 % desligado." },
      ],
    },
    {
      stem: "Na ponte H, o que acontece se as duas chaves do mesmo lado (S1 e S3) forem fechadas ao mesmo tempo?",
      opts: [
        { t: "O motor gira para frente", ok: false, why: "Para o motor girar, a corrente precisa atravessá-lo, ou seja, chaves em diagonal. Duas chaves do mesmo lado não passam pelo motor." },
        { t: "O motor freia", ok: false, why: "O freio é feito fechando as duas chaves de baixo (S3 e S4) ou as duas de cima, colocando os dois terminais do motor no mesmo potencial. Não é o caso aqui." },
        { t: "Curto-circuito direto entre + da bateria e GND (shoot-through), que pode queimar os transistores", ok: true, why: "Correto. Esse caminho não passa pelo motor: a bateria vê um curto. Chips de driver têm proteção contra isso; pontes H montadas à mão, não." },
        { t: "Nada, as chaves são independentes", ok: false, why: "As chaves são independentes eletricamente, mas essa combinação cria um caminho de baixa resistência da bateria ao GND. É a combinação mais perigosa da ponte H." },
      ],
    },
    {
      stem: "Por que o TB6612FNG é preferível ao L298N para motores TT de 6 V?",
      opts: [
        { t: "Porque suporta mais corrente", ok: false, why: "O L298N suporta até 2 A por canal, o TB6612 em regime contínuo 1,2 A. Não é a corrente que decide aqui." },
        { t: "Porque o L298N perde cerca de 2 V internamente, e um motor de 6 V acaba recebendo 4 V; o TB6612 (MOSFET) quase não perde", ok: true, why: "Correto. Em motores de baixa tensão, perder 2 V é perder um terço da tensão e muito torque. O L298N ainda esquenta com essa perda. O TB6612 entrega quase toda a tensão da bateria." },
        { t: "Porque o L298N não aceita PWM", ok: false, why: "Aceita normalmente. A diferença está na perda de tensão e na eficiência." },
        { t: "Porque o TB6612 não precisa de GND comum", ok: false, why: "Todo driver precisa de GND comum com o microcontrolador. Isso não muda." },
      ],
    },
  ],
  3: [
    {
      stem: "Qual largura de pulso leva um servo padrão ao centro (90°)?",
      opts: [
        { t: "1,0 ms", ok: false, why: "1,0 ms é um dos extremos, normalmente 0°." },
        { t: "1,5 ms", ok: true, why: "Correto. O centro do servo corresponde a 1,5 ms, com o pulso repetido a cada 20 ms (50 Hz)." },
        { t: "20 ms", ok: false, why: "20 ms é o período, o intervalo entre pulsos. O que define o ângulo é a largura do pulso dentro desse período." },
        { t: "5 V", ok: false, why: "Volts medem tensão, não tempo. O servo interpreta a duração do pulso, não sua amplitude." },
      ],
    },
    {
      stem: "Você ligou três servos SG90 no pino 5V do Arduino. Ao movê-los juntos, o Arduino reseta. Qual a causa mais provável?",
      opts: [
        { t: "O código tem um bug", ok: false, why: "Pode até ter, mas o sintoma clássico de 'reset ao mover servos' é elétrico: a corrente de pico dos servos derruba a tensão de 5 V e o microcontrolador desliga por um instante." },
        { t: "Os servos estão consumindo mais corrente do que o regulador do Arduino consegue entregar, e a tensão cai", ok: true, why: "Correto. Cada SG90 pode puxar 500 mA ou mais ao começar a mover. Três juntos passam do que o regulador da placa fornece. Alimente os servos por uma fonte separada de 5–6 V com GND comum." },
        { t: "Os servos estão com a polaridade invertida", ok: false, why: "Com polaridade invertida o servo não funciona e pode queimar; ele não faria o Arduino resetar apenas ao mover." },
        { t: "Servos não podem ser usados em Arduino Uno", ok: false, why: "Podem, com a biblioteca Servo.h. O problema é apenas de alimentação." },
      ],
    },
    {
      stem: "Um motor de passo de 1,8° recebeu 400 pulsos de passo (sem micropasso). Quantas voltas ele deu, e como você sabe disso?",
      opts: [
        { t: "2 voltas, porque 360 ÷ 1,8 = 200 passos por volta, e o motor avança um passo conhecido por pulso", ok: true, why: "Correto. Essa é a grande vantagem do motor de passo: contar pulsos dá a posição sem sensor. Desde que ele não tenha perdido passos." },
        { t: "1 volta, porque cada pulso é meio passo", ok: false, why: "Sem micropasso, cada pulso é um passo inteiro de 1,8°. Meio passo só existe se o driver estiver configurado para isso." },
        { t: "Não dá para saber sem um sensor no eixo", ok: false, why: "É justamente o oposto: o motor de passo é escolhido porque não precisa de sensor. A posição é conhecida contando passos (malha aberta)." },
        { t: "4 voltas, porque um pulso é 3,6°", ok: false, why: "O passo declarado é 1,8°. 400 × 1,8° = 720° = 2 voltas." },
      ],
    },
    {
      stem: "Qual é o principal risco de usar motor de passo em um mecanismo com carga próxima do limite?",
      opts: [
        { t: "Ele queima ao travar", ok: false, why: "O motor de passo tolera bem ficar travado com corrente limitada pelo driver. Não é esse o risco." },
        { t: "Ele perde passos sem avisar, e a posição que o programa acha que está deixa de ser a real", ok: true, why: "Correto. Como não há sensor, o programa continua contando passos que não aconteceram. Por isso se usa margem de torque e um sensor de fim de curso para rezerar." },
        { t: "Ele gira ao contrário", ok: false, why: "O sentido é definido pelo pino DIR e não muda com carga." },
        { t: "Ele passa a consumir mais corrente e descarrega a bateria", ok: false, why: "A corrente do motor de passo é limitada pelo driver (Vref) e é praticamente constante, com ou sem carga. O problema da carga é mecânico: perda de passos." },
      ],
    },
  ],
  4: [
    {
      stem: "Um motor brushless de 900 kV alimentado por uma LiPo 3S (11,1 V) gira, sem carga, a aproximadamente:",
      opts: [
        { t: "900 RPM", ok: false, why: "kV é RPM por volt. Faltou multiplicar pela tensão." },
        { t: "10 000 RPM", ok: true, why: "Correto: 900 × 11,1 ≈ 9 990 RPM. E lembre-se: kV alto significa rápido e fraco, kV baixo significa lento e forte." },
        { t: "3 000 RPM", ok: false, why: "3 000 seria 900 × 3,33 V. Uma LiPo 3S tem 11,1 V nominais (3 × 3,7 V)." },
        { t: "Não dá para saber sem o ESC", ok: false, why: "O ESC controla a velocidade, mas a velocidade máxima sem carga é estimada diretamente por kV × tensão." },
      ],
    },
    {
      stem: "Uma bateria diz '2S 2200 mAh 25C'. O que você conclui?",
      opts: [
        { t: "7,4 V nominais, capacidade de 2,2 Ah, e pode entregar até 55 A de pico", ok: true, why: "Correto. 2S = 2 células × 3,7 V; 2200 mAh = 2,2 Ah; 25C × 2,2 Ah = 55 A. É uma bateria de sobra para dois motores TT, e adequada para brushless pequenos." },
        { t: "2 V, 2200 mA de consumo, dura 25 horas", ok: false, why: "'S' conta células em série, não volts. mAh é capacidade, não consumo. 'C' é taxa de descarga, não horas." },
        { t: "7,4 V e ela deve ser carregada até 25 V", ok: false, why: "Uma célula de lítio carrega até 4,2 V, logo 2S carrega até 8,4 V. 25C é a taxa de descarga máxima." },
        { t: "A bateria só serve para motores de 25 A", ok: false, why: "25C é um limite máximo, não um requisito. Ela pode alimentar cargas bem menores." },
      ],
    },
    {
      stem: "Robô de 2 kg, rodas de 100 mm (r = 0,05 m), 2 motores, sobre carpete (μ = 0,4), margem 2. Torque necessário por motor?",
      opts: [
        { t: "≈ 0,4 N·m (≈ 4 kgf·cm)", ok: true, why: "Correto: (2 × 9,8 × 0,05 × 0,4 × 2) ÷ 2 = 0,392 N·m ≈ 4 kgf·cm por motor. Um motor TT (0,8 kgf·cm) não serve; você precisaria de um motor de 12 V com redução maior, como os 'JGA25' ou 'JGB37'." },
        { t: "≈ 0,04 N·m (≈ 0,4 kgf·cm)", ok: false, why: "Faltou um fator de 10. Refaça: 2 × 9,8 × 0,05 = 0,98; × 0,4 = 0,392; × 2 = 0,784; ÷ 2 motores = 0,392 N·m." },
        { t: "≈ 2 N·m", ok: false, why: "Isso seria sem dividir pelos 2 motores e com μ = 1 (o robô subindo uma parede). Sobre piso, μ fica entre 0,2 e 0,4." },
        { t: "Não é possível calcular sem saber a velocidade", ok: false, why: "Velocidade e torque são calculados separadamente. Torque depende de massa, raio da roda e atrito; velocidade depende de RPM e perímetro da roda." },
      ],
    },
    {
      stem: "Você vai montar um robô de duas rodas com sensor ultrassônico em uma 'cabeça' que olha para os lados. Qual combinação de motores e drivers faz sentido?",
      opts: [
        { t: "2 motores de passo NEMA 17 nas rodas e 1 brushless na cabeça", ok: false, why: "Motores de passo nas rodas são pesados, gastam corrente parada e não têm vantagem para andar. Brushless na cabeça é um absurdo: ela precisa ir a um ângulo, não girar rápido." },
        { t: "2 motores TT com um TB6612FNG nas rodas, e 1 servo SG90 na cabeça, com fonte dos motores separada e GND comum", ok: true, why: "Correto. Rodas pedem giro contínuo com controle de velocidade e sentido (DC + ponte H); a cabeça pede ângulo (servo). Fonte separada e GND comum evitam resets." },
        { t: "2 servos de 180° nas rodas e 1 motor TT na cabeça", ok: false, why: "Servo de 180° não gira continuamente, logo não serve para roda (só o de rotação contínua serviria). E o motor TT não sabe parar em um ângulo." },
        { t: "3 motores TT ligados direto nos pinos do Arduino", ok: false, why: "Ligar motor direto no pino foi o primeiro erro que você aprendeu a evitar na Etapa 1. E o motor TT não controla ângulo para a cabeça." },
      ],
    },
  ],
};

// Blocos "Para ir além" — chaveados pela seção do guia
export const refs = {
  "1.1": [
    { k: "vídeo", href: "https://www.youtube.com/watch?v=CWulQ1ZSE3c", t: "How does an Electric Motor work? (Jared Owen)", d: "animação 3D, inglês com legendas; a melhor explicação visual de ímã + bobina + comutador" },
    { k: "vídeo", href: "https://www.youtube.com/watch?v=LAtPHANEfQo", t: "DC Motor, how it works? (Lesics)", d: "inglês com legenda em português; mostra passo a passo por que o rotor continua girando" },
  ],
  "1.2": [
    { k: "leitura", href: "https://learn.sparkfun.com/tutorials/tb6612fng-hookup-guide/all", t: "TB6612FNG Hookup Guide (SparkFun)", d: "inglês; a seção \"Choosing a motor driver\" explica por que dimensionar pela corrente de stall" },
    { k: "leitura", href: "https://www.lojapm.pt/post/guia-completo-escolher-a-bateria-certa-para-o-seu-projeto-li-ion-vs-lipo-vs-nimh-vs-alcalina", t: "Tensão, corrente, capacidade: os termos básicos (Loja PM)", d: "português; a primeira parte define V, A, mAh e taxa C de forma acessível" },
  ],
  "1.3": [
    { k: "leitura", href: "https://portal.vidadesilicio.com.br/driver-motor-com-ponte-h-l298n/", t: "Driver motor com ponte H L298N (Vida de Silício)", d: "português; a introdução explica por que motores são cargas indutivas que o Arduino não aguenta" },
    { k: "leitura", href: "https://www.blogdarobotica.com/2023/05/15/como-controlar-motor-dc-utilizando-o-driver-ponte-h-l298n/", t: "Como controlar motor DC com o driver L298N (Blog da Robótica)", d: "português; primeiro esquema completo de Arduino + driver + pilhas + GND comum" },
  ],
  "2.1": [
    { k: "vídeo", href: "https://www.youtube.com/watch?v=CWulQ1ZSE3c", t: "How does an Electric Motor work? (Jared Owen)", d: "reveja a parte do comutador e das escovas agora que você sabe os nomes das peças" },
    { k: "leitura", href: "https://community.robotshop.com/blog/show/drive-motor-sizing-tool", t: "Drive Motor Sizing Tool (RobotShop)", d: "inglês; o parágrafo introdutório mostra com números o que a redução faz com RPM e torque" },
  ],
  "2.3": [
    { k: "leitura", href: "https://docs.arduino.cc/learn/microcontrollers/analog-output", t: "Basics of PWM (documentação oficial do Arduino)", d: "inglês; curto, com o gráfico clássico de duty cycle e o exemplo Fading" },
    { k: "leitura", href: "https://portal.vidadesilicio.com.br/ponte-h-l298n-controle-velocidade-motor/", t: "Controlando a velocidade de um motor DC com PWM (Vida de Silício)", d: "português; PWM aplicado direto no driver, com código" },
  ],
  "2.4": [
    { k: "vídeo", href: "https://www.youtube.com/watch?v=DAG85jznsGc", t: "Ponte H para pequenos motores DC — Eletrônica para Iniciantes #048 (WR Kits)", d: "português; monta a ponte H com transistores na bancada, o jeito mais claro de entender as 4 chaves" },
    { k: "vídeo", href: "https://www.youtube.com/watch?v=p8tUNIEThb4", t: "Módulo Ponte H: tudo o que você precisa saber (Desafio Maker)", d: "português; foco no módulo pronto e nos erros comuns de ligação" },
  ],
  "2.5": [
    { k: "leitura", href: "https://dronebotworkshop.com/tb6612fng-h-bridge/", t: "TB6612FNG: better than L298N? (DroneBot Workshop)", d: "inglês; compara os dois drivers com medições reais de queda de tensão e temperatura" },
    { k: "leitura", href: "https://lastminuteengineers.com/tb6612fng-dc-motor-control-arduino-tutorial/", t: "Controlling DC motors with TB6612FNG (Last Minute Engineers)", d: "inglês; esquema de ligação claro e código comentado" },
    { k: "leitura", href: "http://blog.filipeflop.com/motores-e-servos/motor-dc-arduino-ponte-h-l298n.html", t: "Motor DC com driver ponte H L298N (FilipeFlop)", d: "português; se você já tem um L298N, é o tutorial de referência" },
  ],
  "2.6": [
    { k: "vídeo", href: "https://www.youtube.com/watch?v=eEf1n8TKNFQ", t: "TB6612FNG motor driver with Arduino (tutorial completo)", d: "inglês; montagem e código do mesmo circuito da seção 2.6, do início ao fim" },
  ],
  "3.1": [
    { k: "leitura", href: "https://www.jameco.com/Jameco/workshop/Howitworks/how-servo-motors-work.html", t: "How servo motors work (Jameco)", d: "inglês; explica o potenciômetro e o controle proporcional dentro do servo em poucas linhas" },
    { k: "leitura", href: "https://blog.robobuilders.com.br/como-usar-o-servo-motor-sg90-mg90-180-com-uma-placa-arduino/", t: "Como usar o servo SG90/MG90 com Arduino (RoboBuilders)", d: "português; ligação, Servo.h e as diferenças entre SG90, MG90 e MG996R" },
    { k: "vídeo", href: "https://theengineeringmindset.com/?p=6681", t: "Servo Motor Explained (The Engineering Mindset)", d: "inglês; artigo com vídeo animado do mecanismo interno" },
  ],
  "3.2": [
    { k: "leitura", href: "https://www.lojapm.pt/post/controlar-um-servo-motor-sg90-com-arduino-tutorial-detalhado", t: "Controlar um servo SG90 com Arduino: tutorial detalhado (Loja PM)", d: "português; dedica uma seção inteira à fonte externa e ao GND comum" },
    { k: "leitura", href: "https://www.arduinoportugal.pt/servo-motores/", t: "Como ligar um servomotor SG90 (Arduino Portugal)", d: "português; explica por que usar Servo.h em vez de analogWrite" },
  ],
  "3.3": [
    { k: "leitura", href: "https://blog.eletrogate.com/guia-completo-do-motor-de-passo-28byj-48-driver-uln2003/", t: "Guia completo do motor de passo 28BYJ-48 + ULN2003 (Eletrogate)", d: "português; o motor de passo de iniciante, com fotos das fases e dos modos de passo" },
    { k: "leitura", href: "https://blog.eletrogate.com/driver-a4988-motor-de-passo-usando-o-arduino/", t: "Tudo sobre o driver A4988 e motor de passo (Eletrogate)", d: "português; micropasso, cálculo do Vref e sketch de teste" },
    { k: "leitura", href: "https://www.makerguides.com/a4988-stepper-motor-driver-arduino-tutorial/", t: "A4988 stepper driver with Arduino (Makerguides)", d: "inglês; o ajuste de Vref com foto de onde encostar o multímetro" },
    { k: "leitura", href: "https://curtocircuito.com.br/blog/driver/controle-de-motor-de-passo-nema-driver-a4988", t: "Controle de motor de passo NEMA com A4988 (Curto Circuito)", d: "português; tabela de micropasso e biblioteca AccelStepper" },
  ],
  "4.1": [
    { k: "leitura", href: "https://howtomechatronics.com/tutorials/arduino/arduino-brushless-motor-control-tutorial-esc-bldc/", t: "Arduino brushless motor control tutorial: ESC & BLDC (How To Mechatronics)", d: "inglês; texto + vídeo; explica kV, ESC, BEC e mostra que o sinal é igual ao de servo" },
    { k: "leitura", href: "https://electricalflux.com/mcu-general/control-brushless-motor-arduino-esc-guide", t: "The complete ESC guide (ElectricalFlux)", d: "inglês; sequência de \"arming\" do ESC e por que ponte H não serve para brushless" },
  ],
  "4.2": [
    { k: "leitura", href: "https://blog.droneng.com.br/baterias-lipo/", t: "O que você deve saber sobre baterias LiPo (DronEng)", d: "português; células, S, mAh e taxa C com exemplos numéricos" },
    { k: "leitura", href: "https://www.escoladedrones.com.br/bateria-lipo-o-que-voce-precisa-saber/", t: "Bateria LiPo: cuidados (Escola de Drones)", d: "português; lista de segurança: balanceador, saco antichama, tensão mínima" },
    { k: "leitura", href: "https://www.lojapm.pt/post/guia-completo-escolher-a-bateria-certa-para-o-seu-projeto-li-ion-vs-lipo-vs-nimh-vs-alcalina", t: "Li-Ion vs LiPo vs NiMH vs alcalina (Loja PM)", d: "português; como calcular a capacidade necessária para a autonomia desejada" },
  ],
  "4.3": [
    { k: "ferramenta", href: "https://community.robotshop.com/blog/show/drive-motor-sizing-tool", t: "Drive Motor Sizing Tool (RobotShop)", d: "inglês; calculadora on-line: massa, raio da roda, velocidade e rampa → torque e RPM por motor" },
    { k: "leitura", href: "https://community.robotshop.com/tutorials/show/drive-motor-sizing-tutorial", t: "Drive Motor Sizing Tutorial (RobotShop)", d: "inglês; as equações completas por trás da calculadora, com aceleração e inclinação" },
  ],
};

// Exercícios extras de fixação — 20 questões por etapa, exibidas em um modal.
// As perguntas têm ordem fixa; as alternativas são embaralhadas no componente.
export const exerciciosExtras = {
  1: [
    {
      stem: "O que acontece quando uma corrente elétrica passa por um fio?",
      opts: [
        { t: "Um campo magnético é criado ao redor do fio", ok: true, why: "É o princípio base de todo motor elétrico." },
        { t: "O fio fica eletricamente isolado", ok: false, why: "Isolamento é propriedade do material, não efeito da corrente." },
        { t: "O fio perde resistência", ok: false, why: "A resistência não some; na prática o fio até esquenta." },
        { t: "Nada, só bobinas produzem efeito magnético", ok: false, why: "Qualquer condutor com corrente gera campo; a bobina só o intensifica." },
      ],
    },
    {
      stem: "Por que o fio do motor é enrolado em forma de bobina?",
      opts: [
        { t: "Para somar o campo de cada volta e criar um campo magnético mais forte", ok: true, why: "Cada espira contribui, e o conjunto vira um eletroímã útil." },
        { t: "Para economizar espaço na carcaça", ok: false, why: "O ganho é magnético, não geométrico." },
        { t: "Para reduzir o consumo de corrente", ok: false, why: "A bobina não existe para economizar corrente." },
        { t: "Para evitar curto-circuito", ok: false, why: "Quem evita curto é o esmalte isolante do fio, não o formato." },
      ],
    },
    {
      stem: "No motor, como se chamam a parte fixa e a parte que gira?",
      opts: [
        { t: "Estator e rotor", ok: true, why: "Estator = estático; rotor = o que rotaciona." },
        { t: "Carcaça e eixo", ok: false, why: "São peças mecânicas, não os nomes dos dois lados eletromagnéticos." },
        { t: "Fonte e carga", ok: false, why: "São termos de circuito elétrico, não de construção de motor." },
        { t: "Comutador e escova", ok: false, why: "São peças específicas do DC escovado, não a divisão fixo/giratório." },
      ],
    },
    {
      stem: "Qual sequência resume o funcionamento de qualquer motor elétrico?",
      opts: [
        { t: "Eletricidade → campo magnético → força → rotação", ok: true, why: "É exatamente a cadeia apresentada na seção 1.1." },
        { t: "Eletricidade → calor → expansão → rotação", ok: false, why: "Calor no motor elétrico é perda, não o mecanismo de giro." },
        { t: "Campo magnético → eletricidade → rotação", ok: false, why: "Essa ordem descreve um gerador, não um motor." },
        { t: "Eletricidade → faísca → explosão → rotação", ok: false, why: "Isso é um motor a combustão." },
      ],
    },
    {
      stem: "Qual par grandeza/unidade está correto?",
      opts: [
        { t: "Tensão em volts e corrente em ampères", ok: true, why: "V para tensão, A para corrente — os dois primeiros itens de toda ficha técnica." },
        { t: "Tensão em watts e corrente em volts", ok: false, why: "Watt mede potência, não tensão." },
        { t: "Tensão em ampères e corrente em volts", ok: false, why: "Está invertido." },
        { t: "Tensão em N·m e corrente em RPM", ok: false, why: "N·m é torque e RPM é velocidade de rotação." },
      ],
    },
    {
      stem: "Um motor de 6 V consumindo 2 A dissipa que potência?",
      opts: [
        { t: "12 W", ok: true, why: "P = V × I = 6 × 2 = 12 W." },
        { t: "3 W", ok: false, why: "3 seria 6 ÷ 2; potência é o produto, não a divisão." },
        { t: "8 W", ok: false, why: "6 + 2 = 8 soma grandezas diferentes, o que não tem significado físico." },
        { t: "Não dá para calcular sem o torque", ok: false, why: "A potência elétrica sai direto de V × I." },
      ],
    },
    {
      stem: "Na analogia com água, tensão e corrente correspondem a:",
      opts: [
        { t: "Pressão na mangueira e vazão de água", ok: true, why: "Tensão empurra (pressão); corrente é o quanto flui (vazão)." },
        { t: "Vazão e pressão", ok: false, why: "Está invertido." },
        { t: "Temperatura e volume", ok: false, why: "Não fazem parte da analogia elétrica." },
        { t: "Profundidade e correnteza", ok: false, why: "A analogia clássica usa pressão e vazão." },
      ],
    },
    {
      stem: "5 kgf·cm equivalem a aproximadamente:",
      opts: [
        { t: "0,49 N·m", ok: true, why: "1 kgf·cm ≈ 0,098 N·m (\"quase 0,1\"), então 5 × 0,098 ≈ 0,49." },
        { t: "5 N·m", ok: false, why: "Seria tratar as unidades como iguais; kgf·cm é ~10× menor que N·m." },
        { t: "50 N·m", ok: false, why: "Multiplicou na direção errada da conversão." },
        { t: "0,05 N·m", ok: false, why: "Um kgf·cm vale quase 0,1 N·m, não 0,01." },
      ],
    },
    {
      stem: "Ligar um motor de 6 V em uma fonte de 12 V tende a:",
      opts: [
        { t: "Fazer o motor girar além do projetado, esquentar e queimar", ok: true, why: "O dobro da tensão força corrente e rotação acima do projeto." },
        { t: "Funcionar normalmente, motores aceitam qualquer tensão", ok: false, why: "Cada motor é projetado para uma faixa de tensão." },
        { t: "Girar mais devagar", ok: false, why: "Tensão maior aumenta a velocidade, não diminui." },
        { t: "Não girar", ok: false, why: "Ele gira — rápido demais — e superaquece." },
      ],
    },
    {
      stem: "E ligar o mesmo motor de 6 V em 3 V?",
      opts: [
        { t: "Gira fraco e devagar, sem danos", ok: true, why: "Metade da tensão = menos velocidade e torque, mas nada queima." },
        { t: "Queima", ok: false, why: "Tensão abaixo da nominal não danifica o motor." },
        { t: "Gira na velocidade normal com menos torque", ok: false, why: "A velocidade também cai com a tensão." },
        { t: "Inverte o sentido", ok: false, why: "O sentido depende da polaridade, não do valor da tensão." },
      ],
    },
    {
      stem: "Por que o RPM sozinho não basta para escolher o motor de um robô?",
      opts: [
        { t: "Porque motor rápido costuma ser fraco: sem o torque, o número não diz se ele move o robô", ok: true, why: "Velocidade e torque andam em direções opostas; é preciso olhar os dois." },
        { t: "Porque o RPM muda conforme a marca", ok: false, why: "O RPM declarado é medível; o problema é ele não contar a história toda." },
        { t: "Porque RPM só vale para brushless", ok: false, why: "Todo motor rotativo tem RPM." },
        { t: "Porque rodas não dependem de RPM", ok: false, why: "Dependem: velocidade linear = RPM × perímetro ÷ 60." },
      ],
    },
    {
      stem: "A corrente 'sem carga' (no-load) é medida com:",
      opts: [
        { t: "O eixo girando livre, sem nada acoplado", ok: true, why: "É o cenário de menor esforço e menor corrente." },
        { t: "O motor travado", ok: false, why: "Motor travado é o oposto: corrente de stall." },
        { t: "O robô subindo uma rampa", ok: false, why: "Rampa é carga alta, perto do stall." },
        { t: "O motor desligado", ok: false, why: "Desligado, a corrente é zero." },
      ],
    },
    {
      stem: "Em que momentos a corrente real se aproxima do valor de stall?",
      opts: [
        { t: "Na partida e quando o eixo trava", ok: true, why: "Parado ou travado, o motor puxa o máximo que a fonte permite." },
        { t: "Somente em bancada de teste", ok: false, why: "Acontece em uso real toda vez que o robô arranca ou prende." },
        { t: "Quando o motor gira livre", ok: false, why: "Girando livre a corrente é mínima." },
        { t: "Quando o PWM está em 50 %", ok: false, why: "O duty não define o pico; a carga mecânica define." },
      ],
    },
    {
      stem: "O 'chute indutivo' é:",
      opts: [
        { t: "Um pico de tensão que a bobina devolve quando a corrente é cortada", ok: true, why: "A energia armazenada no campo magnético precisa ir para algum lugar." },
        { t: "O tranco mecânico da partida", ok: false, why: "O chute indutivo é elétrico, não mecânico." },
        { t: "A vibração das escovas", ok: false, why: "Escovas geram ruído elétrico, mas o chute vem da bobina." },
        { t: "A queda de tensão da bateria", ok: false, why: "É o fenômeno oposto: um pico, não uma queda." },
      ],
    },
    {
      stem: "O que protege o circuito contra o chute indutivo?",
      opts: [
        { t: "Diodos de roda livre (flyback), já incluídos nos drivers modernos", ok: true, why: "Eles dão um caminho seguro para a energia da bobina." },
        { t: "Um fusível", ok: false, why: "Fusível age contra sobrecorrente sustentada; é lento demais para o pico." },
        { t: "O GND comum", ok: false, why: "GND comum é referência de sinal, não proteção." },
        { t: "Um resistor em série", ok: false, why: "Limita corrente média, mas não absorve o pico da bobina." },
      ],
    },
    {
      stem: "Por que o GND do Arduino e o do driver precisam estar interligados?",
      opts: [
        { t: "Porque tensão é diferença entre dois pontos: sem referência comum, o sinal de controle não significa nada", ok: true, why: "O HIGH de 5 V só é HIGH em relação a um 0 V compartilhado." },
        { t: "Para carregar a bateria do robô", ok: false, why: "GND não transfere carga entre fontes." },
        { t: "Para dobrar a corrente disponível", ok: false, why: "Ligar GNDs não soma capacidade de corrente." },
        { t: "Não precisam, é opcional", ok: false, why: "Sem GND comum o driver não interpreta o sinal — é o erro clássico." },
      ],
    },
    {
      stem: "Quem faz a sequência de liga/desliga das bobinas em um motor DC escovado?",
      opts: [
        { t: "O comutador, mecanicamente, junto com as escovas", ok: true, why: "É a solução mecânica embutida no próprio motor." },
        { t: "Um chip interno com sensor", ok: false, why: "Esse é o caso do servo." },
        { t: "O ESC", ok: false, why: "ESC é o driver do brushless." },
        { t: "O próprio Arduino", ok: false, why: "O Arduino nem sabe em que posição o rotor está." },
      ],
    },
    {
      stem: "Qual é o nome do driver de cada motor (DC / passo / brushless)?",
      opts: [
        { t: "Ponte H / driver de passo / ESC", ok: true, why: "Cada tipo tem seu chaveador de potência com nome próprio." },
        { t: "ESC / ponte H / driver de passo", ok: false, why: "A ordem está trocada." },
        { t: "Todos usam ESC", ok: false, why: "ESC é específico do brushless." },
        { t: "Nenhum precisa de driver", ok: false, why: "Todos precisam; só o servo já o traz embutido." },
      ],
    },
    {
      stem: "Qual motor já sai de fábrica com driver e sensor embutidos?",
      opts: [
        { t: "Servo", ok: true, why: "Motor + redução + potenciômetro + placa de controle na mesma carcaça." },
        { t: "Motor TT", ok: false, why: "É só motor + redução; o driver fica fora." },
        { t: "NEMA 17", ok: false, why: "Precisa de A4988/DRV8825 externo." },
        { t: "Brushless", ok: false, why: "Precisa de um ESC externo." },
      ],
    },
    {
      stem: "Para girar as rodas de um robô pequeno continuamente, o tipo mais indicado é:",
      opts: [
        { t: "DC escovado com caixa de redução", ok: true, why: "Barato, eficiente e controlável com PWM + ponte H." },
        { t: "Servo de 180°", ok: false, why: "Não gira continuamente." },
        { t: "Motor de passo", ok: false, why: "Pesado e gastador para giro contínuo de rodas." },
        { t: "Brushless de drone", ok: false, why: "Exagero para um robô pequeno; kV alto e pouca força em baixa rotação." },
      ],
    },
  ],
  2: [
    {
      stem: "Qual é a função do comutador no motor DC escovado?",
      opts: [
        { t: "Inverter o sentido da corrente na bobina a cada meia volta, mantendo o giro", ok: true, why: "Sem essa inversão o rotor pararia alinhado ao campo." },
        { t: "Reduzir a velocidade do eixo", ok: false, why: "Quem faz isso é a caixa de redução." },
        { t: "Medir a posição do rotor", ok: false, why: "O DC escovado não tem sensor de posição." },
        { t: "Aumentar a tensão da bateria", ok: false, why: "Nenhuma peça do motor eleva tensão." },
      ],
    },
    {
      stem: "Qual peça do motor escovado se desgasta com o uso?",
      opts: [
        { t: "As escovas de carvão", ok: true, why: "O atrito constante com o comutador as consome aos poucos." },
        { t: "Os ímãs permanentes", ok: false, why: "Ímãs não se desgastam por atrito; ficam fixos no estator." },
        { t: "O eixo", ok: false, why: "O eixo gira em mancais e dura muito mais que as escovas." },
        { t: "A carcaça", ok: false, why: "A carcaça é estrutural, não participa do contato elétrico." },
      ],
    },
    {
      stem: "Como se inverte o sentido de giro de um motor DC?",
      opts: [
        { t: "Invertendo a polaridade nos dois fios", ok: true, why: "É a base da ponte H." },
        { t: "Aumentando o PWM", ok: false, why: "PWM controla velocidade, não sentido." },
        { t: "Trocando a bateria por uma maior", ok: false, why: "Mais tensão = mais velocidade, mesmo sentido." },
        { t: "Não é possível inverter", ok: false, why: "É possível e trivial: basta inverter a polaridade." },
      ],
    },
    {
      stem: "O motor TT (redução 1:48) em 6 V entrega na roda cerca de:",
      opts: [
        { t: "200 RPM e 0,8 kgf·cm", ok: true, why: "São os números de referência da seção 2.2." },
        { t: "10 000 RPM e 5 kgf·cm", ok: false, why: "10 000 RPM é o motor interno antes da redução." },
        { t: "48 RPM e 48 kgf·cm", ok: false, why: "1:48 é a relação de redução, não os valores de saída." },
        { t: "3 000 RPM e 0,1 kgf·cm", ok: false, why: "Rápido e fraco assim seria um motor sem redução nenhuma." },
      ],
    },
    {
      stem: "A caixa de redução:",
      opts: [
        { t: "Divide a velocidade e multiplica o torque pelo mesmo fator", ok: true, why: "Menos as perdas de atrito nas engrenagens." },
        { t: "Multiplica velocidade e torque", ok: false, why: "Seria energia de graça — impossível." },
        { t: "Divide velocidade e torque", ok: false, why: "O torque aumenta, não diminui." },
        { t: "Só reduz o consumo elétrico", ok: false, why: "A troca é mecânica: velocidade por torque." },
      ],
    },
    {
      stem: "Trocando a redução de 1:48 para 1:90, a roda fica:",
      opts: [
        { t: "Mais lenta e mais forte", ok: true, why: "Redução maior = mais voltas do motor por volta da roda." },
        { t: "Mais rápida e mais forte", ok: false, why: "Não dá para ganhar os dois ao mesmo tempo." },
        { t: "Mais lenta e mais fraca", ok: false, why: "A força aumenta junto com a redução." },
        { t: "Igual — redução não muda nada", ok: false, why: "Muda exatamente velocidade e torque." },
      ],
    },
    {
      stem: "Por que o motor responde à média do PWM e não a cada pulso?",
      opts: [
        { t: "A inércia mecânica e a indutância elétrica filtram o chaveamento rápido", ok: true, why: "O rotor não consegue acelerar e frear milhares de vezes por segundo." },
        { t: "O driver converte o PWM em tensão contínua exata", ok: false, why: "O driver também só chaveia; a filtragem é do próprio motor." },
        { t: "O Arduino envia tensão analógica de verdade", ok: false, why: "analogWrite gera PWM, não tensão analógica." },
        { t: "Os pulsos são aleatórios e se cancelam", ok: false, why: "Os pulsos são regulares; é a média que importa." },
      ],
    },
    {
      stem: "analogWrite(pino, 255) corresponde a que duty cycle?",
      opts: [
        { t: "100 %", ok: true, why: "A escala vai de 0 (0 %) a 255 (100 %)." },
        { t: "50 %", ok: false, why: "50 % é aproximadamente 128." },
        { t: "25,5 %", ok: false, why: "Confundiu a escala 0–255 com porcentagem." },
        { t: "Depende do motor", ok: false, why: "O duty é definido pelo sinal, não pelo motor." },
      ],
    },
    {
      stem: "Para ~25 % de duty cycle, o valor de analogWrite é aproximadamente:",
      opts: [
        { t: "64", ok: true, why: "25 % de 255 ≈ 64." },
        { t: "25", ok: false, why: "A escala é 0–255, não 0–100." },
        { t: "128", ok: false, why: "128 é 50 %." },
        { t: "255", ok: false, why: "255 é 100 %." },
      ],
    },
    {
      stem: "Motor de 6 V com duty de 75 %: qual a tensão média?",
      opts: [
        { t: "4,5 V", ok: true, why: "6 × 0,75 = 4,5 V." },
        { t: "6 V", ok: false, why: "6 V seria 100 % de duty." },
        { t: "1,5 V", ok: false, why: "1,5 V corresponderia a 25 %." },
        { t: "7,5 V", ok: false, why: "A média nunca passa da tensão da fonte." },
      ],
    },
    {
      stem: "Com duty muito baixo (10–20 %), um motor com redução costuma:",
      opts: [
        { t: "Ficar parado zumbindo, sem vencer o atrito", ok: true, why: "A tensão média é real, mas insuficiente para o arranque mecânico." },
        { t: "Girar na velocidade proporcional exata", ok: false, why: "A proporcionalidade falha perto do limite de atrito." },
        { t: "Queimar", ok: false, why: "Duty baixo não danifica o motor." },
        { t: "Inverter o sentido", ok: false, why: "Sentido não depende do duty." },
      ],
    },
    {
      stem: "Quantas chaves eletrônicas formam uma ponte H?",
      opts: [
        { t: "4", ok: true, why: "Duas em cima e duas embaixo, com o motor na barra do meio." },
        { t: "2", ok: false, why: "Com duas chaves só se liga/desliga, sem inverter." },
        { t: "6", ok: false, why: "Seis chaves aparecem em inversores trifásicos (brushless)." },
        { t: "8", ok: false, why: "Oito seriam duas pontes H (dois motores)." },
      ],
    },
    {
      stem: "Para o motor girar, as chaves fechadas devem estar:",
      opts: [
        { t: "Em diagonal — uma em cima de um lado, outra embaixo do outro", ok: true, why: "Só assim a corrente atravessa o motor." },
        { t: "As duas de cima", ok: false, why: "Isso coloca os dois terminais no mesmo potencial: é freio, não giro." },
        { t: "As duas do mesmo lado", ok: false, why: "Isso é shoot-through: curto da bateria." },
        { t: "Todas as quatro", ok: false, why: "Curto duplo — o pior cenário possível." },
      ],
    },
    {
      stem: "O modo 'freio' da ponte H fecha:",
      opts: [
        { t: "As duas chaves de baixo (ou as duas de cima), curto-circuitando o motor", ok: true, why: "O motor vira um gerador em curto e para rápido." },
        { t: "As chaves em diagonal", ok: false, why: "Diagonal é giro (frente ou ré)." },
        { t: "Nenhuma chave", ok: false, why: "Nenhuma chave é o modo livre (coast)." },
        { t: "As duas do mesmo lado", ok: false, why: "Isso é shoot-through, não freio." },
      ],
    },
    {
      stem: "No modo 'livre' (coast):",
      opts: [
        { t: "Nenhuma chave fecha e o motor para devagar, por atrito", ok: true, why: "O motor fica desconectado eletricamente." },
        { t: "O motor para instantaneamente", ok: false, why: "Parada rápida é o freio." },
        { t: "O motor acelera", ok: false, why: "Sem energia não há aceleração." },
        { t: "A bateria desconecta do robô inteiro", ok: false, why: "Só o motor fica solto; o resto continua alimentado." },
      ],
    },
    {
      stem: "Shoot-through é:",
      opts: [
        { t: "Curto entre + e GND pela mesma perna da ponte (S1+S3 ou S2+S4)", ok: true, why: "A corrente pula o motor e a bateria vê um curto direto." },
        { t: "Disparar o PWM acima de 100 %", ok: false, why: "Duty maior que 100 % não existe." },
        { t: "A faísca nas escovas do motor", ok: false, why: "Faísca de escova é normal no DC escovado." },
        { t: "Sobrecarga da caixa de redução", ok: false, why: "Shoot-through é um defeito elétrico, não mecânico." },
      ],
    },
    {
      stem: "A principal desvantagem do L298N para motores de 6 V é:",
      opts: [
        { t: "Perder cerca de 2 V internamente — o motor recebe só ~4 V", ok: true, why: "Um terço da tensão vira calor no driver." },
        { t: "Não suportar PWM", ok: false, why: "Suporta normalmente." },
        { t: "Controlar só um motor", ok: false, why: "Controla dois." },
        { t: "Não ter proteção nenhuma", ok: false, why: "Tem proteções; o problema é a perda de tensão." },
      ],
    },
    {
      stem: "Para um motor muito grande (dezenas de ampères), qual driver da tabela?",
      opts: [
        { t: "BTS7960 (até 43 A)", ok: true, why: "É o indicado para cadeira de rodas e robô de combate." },
        { t: "TB6612FNG", ok: false, why: "Aguenta 1,2 A contínuos — ótimo para motores TT, pequeno demais aqui." },
        { t: "DRV8833", ok: false, why: "1,5 A: mesma classe do TB6612." },
        { t: "ULN2003", ok: false, why: "É o driver do motor de passo 28BYJ-48." },
      ],
    },
    {
      stem: "Os diodos de roda livre (flyback) servem para:",
      opts: [
        { t: "Dar caminho seguro ao pico de tensão da bobina quando as chaves abrem", ok: true, why: "Absorvem o chute indutivo da Etapa 1." },
        { t: "Aumentar o torque", ok: false, why: "Não têm efeito mecânico." },
        { t: "Regular a tensão da bateria", ok: false, why: "Isso é papel de um regulador." },
        { t: "Impedir que o motor inverta", ok: false, why: "A inversão é controlada pela ponte H." },
      ],
    },
    {
      stem: "No TB6612FNG, o pino STBY precisa estar:",
      opts: [
        { t: "Em nível alto (HIGH) para o driver sair do modo de espera", ok: true, why: "É a primeira linha do setup() no código da seção 2.6." },
        { t: "Em GND", ok: false, why: "Em GND o driver fica dormindo e nada funciona." },
        { t: "Ligado ao motor", ok: false, why: "STBY é pino de controle, não de potência." },
        { t: "Desconectado", ok: false, why: "Pino flutuando dá comportamento imprevisível." },
      ],
    },
  ],
  3: [
    {
      stem: "O que mede o ângulo atual dentro de um servo de hobby?",
      opts: [
        { t: "Um potenciômetro preso ao eixo de saída", ok: true, why: "É o sensor da malha fechada do servo." },
        { t: "Um encoder óptico", ok: false, why: "Comum em servos industriais, não nos de hobby." },
        { t: "Um acelerômetro", ok: false, why: "Acelerômetro mede aceleração, não ângulo de eixo." },
        { t: "O próprio Arduino", ok: false, why: "O Arduino só envia o pulso; quem mede é o servo." },
      ],
    },
    {
      stem: "'Malha fechada' significa que o servo:",
      opts: [
        { t: "Mede o ângulo real e corrige até coincidir com o pedido", ok: true, why: "Medição + comparação + correção contínua." },
        { t: "Tem a carcaça lacrada", ok: false, why: "Malha se refere ao controle, não à mecânica." },
        { t: "Gira em círculo completo", ok: false, why: "O servo padrão gira só 0–180°." },
        { t: "Só funciona com sinal contínuo", ok: false, why: "O sinal é pulsado, a 50 Hz." },
      ],
    },
    {
      stem: "O pulso de controle do servo se repete a cada:",
      opts: [
        { t: "20 ms (50 Hz)", ok: true, why: "É o período padrão do sinal de servo." },
        { t: "1 ms", ok: false, why: "1 ms é a largura mínima do pulso, não o período." },
        { t: "1 segundo", ok: false, why: "Lento demais — o servo tremeria." },
        { t: "100 µs", ok: false, why: "Rápido demais; não é o padrão." },
      ],
    },
    {
      stem: "Um pulso de 1,0 ms leva o servo padrão para:",
      opts: [
        { t: "0°", ok: true, why: "1,0 ms é um dos extremos da faixa." },
        { t: "90°", ok: false, why: "90° corresponde a 1,5 ms." },
        { t: "180°", ok: false, why: "180° corresponde a 2,0 ms." },
        { t: "Rotação contínua", ok: false, why: "Servo padrão nunca gira continuamente." },
      ],
    },
    {
      stem: "E um pulso de 2,0 ms?",
      opts: [
        { t: "180°", ok: true, why: "É o outro extremo da faixa." },
        { t: "0°", ok: false, why: "0° é 1,0 ms." },
        { t: "90°", ok: false, why: "90° é 1,5 ms." },
        { t: "Desliga o servo", ok: false, why: "O servo desligaria sem pulso nenhum, não com 2,0 ms." },
      ],
    },
    {
      stem: "Um pulso de 1,25 ms posiciona o servo em torno de:",
      opts: [
        { t: "45°", ok: true, why: "A escala é linear: 0,25 ms acima do mínimo ≈ um quarto de 180°." },
        { t: "25°", ok: false, why: "Confundiu os 0,25 ms com graus diretos." },
        { t: "125°", ok: false, why: "Leu o 1,25 como se fosse o ângulo." },
        { t: "90°", ok: false, why: "90° seria 1,5 ms." },
      ],
    },
    {
      stem: "Nos três fios do servo, o laranja/amarelo/branco é:",
      opts: [
        { t: "O sinal", ok: true, why: "Marrom/preto = GND, vermelho = alimentação, o claro = sinal." },
        { t: "O GND", ok: false, why: "GND é o marrom ou preto." },
        { t: "A alimentação", ok: false, why: "Alimentação é o vermelho." },
        { t: "Tanto faz, a ordem não importa", ok: false, why: "Inverter fios pode queimar o servo." },
      ],
    },
    {
      stem: "No servo de rotação contínua, o pulso controla:",
      opts: [
        { t: "Velocidade e sentido, não o ângulo", ok: true, why: "Sem o potenciômetro, o erro nunca zera e o motor gira sempre." },
        { t: "O ângulo, com mais precisão", ok: false, why: "Ele perdeu justamente a noção de ângulo." },
        { t: "O torque", ok: false, why: "Torque não é controlado pelo pulso." },
        { t: "Nada — ele gira sozinho", ok: false, why: "Sem pulso ele fica parado (1,5 ms = parado)." },
      ],
    },
    {
      stem: "Qual a diferença do MG90S para o SG90?",
      opts: [
        { t: "Engrenagens de metal: mesmo tamanho, dura mais", ok: true, why: "É a evolução direta do SG90." },
        { t: "É três vezes maior", ok: false, why: "Os dois têm o mesmo formato de 9 g." },
        { t: "É de rotação contínua", ok: false, why: "Ambos são servos de posição." },
        { t: "Usa outro tipo de sinal", ok: false, why: "O sinal é o mesmo padrão de 1–2 ms." },
      ],
    },
    {
      stem: "Para um braço robótico pequeno que levanta objetos, o servo indicado é:",
      opts: [
        { t: "MG996R (≈ 10 kgf·cm)", ok: true, why: "Tem o torque necessário e engrenagens de metal." },
        { t: "SG90", ok: false, why: "1,8 kgf·cm e engrenagem de plástico: quebraria com carga." },
        { t: "MG90S", ok: false, why: "Mais durável que o SG90, mas o torque é quase o mesmo." },
        { t: "Qualquer um serve", ok: false, why: "O torque varia mais de 5× entre eles." },
      ],
    },
    {
      stem: "Três servos ligados ao pino 5V do Arduino resetam a placa porque:",
      opts: [
        { t: "O pico de corrente deles derruba a tensão do regulador", ok: true, why: "Cada servo pode puxar 500 mA ao partir." },
        { t: "O código trava", ok: false, why: "O sintoma é elétrico, não de software." },
        { t: "O sinal de um interfere no outro", ok: false, why: "Cada servo tem seu pino de sinal independente." },
        { t: "Servos não funcionam em Arduino Uno", ok: false, why: "Funcionam bem — com alimentação adequada." },
      ],
    },
    {
      stem: "Um motor de passo com passo de 1,8° tem quantos passos por volta?",
      opts: [
        { t: "200", ok: true, why: "360 ÷ 1,8 = 200." },
        { t: "180", ok: false, why: "Seria o caso de um passo de 2°." },
        { t: "100", ok: false, why: "100 passos de 1,8° dão só meia volta." },
        { t: "360", ok: false, why: "360 passos exigiriam passo de 1°." },
      ],
    },
    {
      stem: "100 passos de 1,8° giram o eixo:",
      opts: [
        { t: "180° (meia volta)", ok: true, why: "100 × 1,8° = 180°." },
        { t: "100°", ok: false, why: "Esqueceu de multiplicar pelo ângulo do passo." },
        { t: "360°", ok: false, why: "Uma volta completa pede 200 passos." },
        { t: "18°", ok: false, why: "Errou a ordem de grandeza." },
      ],
    },
    {
      stem: "'Malha aberta' no motor de passo significa:",
      opts: [
        { t: "A posição é estimada contando passos, sem sensor para confirmar", ok: true, why: "É a força e a fraqueza do motor de passo." },
        { t: "O motor não tem carcaça", ok: false, why: "Malha se refere ao controle, não à mecânica." },
        { t: "O driver fica exposto", ok: false, why: "Nada a ver com a montagem física." },
        { t: "As bobinas ficam sempre energizadas", ok: false, why: "Isso é corrente de retenção, outro conceito." },
      ],
    },
    {
      stem: "O motor de passo perde passos quando:",
      opts: [
        { t: "A carga ou a aceleração excedem o torque disponível", ok: true, why: "O rotor não acompanha a sequência e pula posições em silêncio." },
        { t: "A bateria está carregada demais", ok: false, why: "Carga da bateria não causa perda de passos." },
        { t: "O micropasso está ativado", ok: false, why: "Micropasso reduz um pouco o torque, mas a causa é exigir além do disponível." },
        { t: "O fim de curso está instalado", ok: false, why: "O fim de curso existe justamente para mitigar o problema." },
      ],
    },
    {
      stem: "Para que serve o sensor de fim de curso em sistemas com motor de passo?",
      opts: [
        { t: "Definir a posição zero na partida, já que o motor não sabe onde está", ok: true, why: "É o 'homing' de toda impressora 3D." },
        { t: "Aumentar o torque disponível", ok: false, why: "Sensor não altera a mecânica." },
        { t: "Contar as voltas durante o movimento", ok: false, why: "Quem conta passos é o controlador; o fim de curso só marca o zero." },
        { t: "Frear o motor", ok: false, why: "Freio não é função de sensor." },
      ],
    },
    {
      stem: "O 28BYJ-48 normalmente vem acompanhado de qual driver?",
      opts: [
        { t: "ULN2003", ok: true, why: "A plaquinha com 4 LEDs que vem no mesmo kit." },
        { t: "A4988", ok: false, why: "A4988 é para motores bipolares como o NEMA 17." },
        { t: "TB6612FNG", ok: false, why: "É driver de motor DC; o par clássico do 28BYJ-48 (unipolar, 5 fios) é o ULN2003." },
        { t: "ESC", ok: false, why: "ESC é para brushless." },
      ],
    },
    {
      stem: "Micropasso no A4988: o que se ganha e o que se perde?",
      opts: [
        { t: "Ganha suavidade e resolução; perde um pouco de torque", ok: true, why: "É a troca descrita na seção 3.3." },
        { t: "Ganha torque; perde velocidade", ok: false, why: "O torque diminui, não aumenta." },
        { t: "Ganha velocidade; perde precisão", ok: false, why: "A resolução aumenta — o oposto de perder precisão." },
        { t: "Não muda nada na prática", ok: false, why: "A diferença de suavidade é bem audível e visível." },
      ],
    },
    {
      stem: "O ajuste de Vref no A4988 define:",
      opts: [
        { t: "A corrente máxima entregue ao motor", ok: true, why: "Sem ajustar, o motor esquenta ou o driver desliga por proteção." },
        { t: "O número de micropassos", ok: false, why: "Micropasso é definido pelos pinos MS1–MS3." },
        { t: "A direção de giro", ok: false, why: "Direção é o pino DIR." },
        { t: "A tensão da parte lógica", ok: false, why: "A lógica usa a alimentação de 3,3/5 V, não o Vref." },
      ],
    },
    {
      stem: "Garra que vai até 90° e plotter que precisa de posição exata: qual dupla de motores?",
      opts: [
        { t: "Servo para a garra; motor de passo para o plotter", ok: true, why: "Ângulo limitado com força = servo; posição repetitiva em muitas voltas = passo." },
        { t: "Passo para a garra; servo para o plotter", ok: false, why: "Está invertido: o servo não cobre as muitas voltas do plotter." },
        { t: "DC com redução para os dois", ok: false, why: "DC não sabe posição sem sensores extras." },
        { t: "Brushless para os dois", ok: false, why: "Brushless é motor de velocidade, não de posição." },
      ],
    },
  ],
  4: [
    {
      stem: "No motor brushless, onde ficam os ímãs e as bobinas?",
      opts: [
        { t: "Ímãs no rotor, bobinas no estator — o contrário do DC escovado", ok: true, why: "Por isso não precisa de escovas para levar corrente à parte que gira." },
        { t: "Ímãs no estator, bobinas no rotor", ok: false, why: "Essa é a construção do DC escovado." },
        { t: "Ambos no rotor", ok: false, why: "Aí nada reagiria contra nada." },
        { t: "Ambos no estator", ok: false, why: "O rotor precisa de uma das duas partes." },
      ],
    },
    {
      stem: "Quem energiza as bobinas do brushless na sequência certa?",
      opts: [
        { t: "O ESC (Electronic Speed Controller)", ok: true, why: "Faz eletronicamente o que o comutador fazia mecanicamente." },
        { t: "O comutador", ok: false, why: "Brushless não tem comutador — é o ponto da tecnologia." },
        { t: "As escovas", ok: false, why: "Brushless significa literalmente 'sem escovas'." },
        { t: "Um potenciômetro", ok: false, why: "Potenciômetro é o sensor do servo." },
      ],
    },
    {
      stem: "Como inverter o sentido de um motor brushless?",
      opts: [
        { t: "Trocando dois dos três fios entre si", ok: true, why: "Inverte a ordem da sequência trifásica." },
        { t: "Invertendo os dois fios", ok: false, why: "Brushless tem três fios, não dois." },
        { t: "Enviando pulso menor que 1 ms", ok: false, why: "Abaixo de 1 ms o ESC entende 'parado', não 'ré'." },
        { t: "Não é possível", ok: false, why: "É possível e é o método padrão." },
      ],
    },
    {
      stem: "O sinal que o microcontrolador envia ao ESC é:",
      opts: [
        { t: "O mesmo pulso de 1 a 2 ms usado em servos — dá para usar Servo.h", ok: true, why: "1 ms = parado, 2 ms = velocidade máxima." },
        { t: "PWM de potência direto nas bobinas", ok: false, why: "Quem chaveia a potência é o ESC, não o microcontrolador." },
        { t: "Um barramento I2C", ok: false, why: "ESCs comuns usam o pulso de servo, não I2C." },
        { t: "Tensão analógica proporcional", ok: false, why: "O controle é por largura de pulso." },
      ],
    },
    {
      stem: "kV em um motor brushless significa:",
      opts: [
        { t: "RPM por volt, sem carga", ok: true, why: "1000 kV em 10 V ≈ 10 000 RPM." },
        { t: "Quilovolts", ok: false, why: "Apesar da grafia, não tem relação com milhares de volts." },
        { t: "A corrente máxima", ok: false, why: "Corrente máxima é outro item da ficha." },
        { t: "O torque por volt", ok: false, why: "kV fala de velocidade; o torque vai na direção oposta." },
      ],
    },
    {
      stem: "Um motor de 2000 kV em uma bateria 2S (7,4 V) gira sem carga a cerca de:",
      opts: [
        { t: "14 800 RPM", ok: true, why: "2000 × 7,4 = 14 800 RPM." },
        { t: "2 000 RPM", ok: false, why: "Faltou multiplicar pela tensão." },
        { t: "7 400 RPM", ok: false, why: "Usou só a tensão, sem o kV." },
        { t: "270 RPM", ok: false, why: "Dividiu em vez de multiplicar." },
      ],
    },
    {
      stem: "Um motor de kV baixo é:",
      opts: [
        { t: "Mais lento e mais forte", ok: true, why: "kV baixo = menos RPM por volt e mais torque." },
        { t: "Mais rápido e mais fraco", ok: false, why: "Essa é a descrição de kV alto." },
        { t: "Sempre fisicamente menor", ok: false, why: "kV não determina o tamanho." },
        { t: "Incompatível com ESC", ok: false, why: "Todo brushless usa ESC, qualquer kV." },
      ],
    },
    {
      stem: "Brushless em robô com rodas compensa quando:",
      opts: [
        { t: "Você precisa de muita potência em pouco peso (combate, corrida)", ok: true, why: "Fora isso, o DC com redução é mais simples e barato." },
        { t: "Sempre — é a tecnologia mais moderna", ok: false, why: "Moderno não significa adequado para todo projeto." },
        { t: "Nunca — brushless é só para drones", ok: false, why: "Robôs de combate usam brushless nas rodas." },
        { t: "Só com rodas de 65 mm", ok: false, why: "O diâmetro da roda não define a tecnologia do motor." },
      ],
    },
    {
      stem: "Uma bateria 3S tem tensão nominal de:",
      opts: [
        { t: "11,1 V (3 × 3,7 V)", ok: true, why: "S conta células de lítio em série." },
        { t: "3 V", ok: false, why: "Confundiu o número de células com a tensão." },
        { t: "7,4 V", ok: false, why: "7,4 V é uma 2S." },
        { t: "12,6 V", ok: false, why: "12,6 V é a 3S totalmente carregada (3 × 4,2), não a nominal." },
      ],
    },
    {
      stem: "Uma célula de lítio totalmente carregada chega a:",
      opts: [
        { t: "4,2 V", ok: true, why: "É o teto de carga por célula." },
        { t: "3,7 V", ok: false, why: "3,7 V é a tensão nominal, não a máxima." },
        { t: "5 V", ok: false, why: "Carregar até 5 V destruiria a célula." },
        { t: "3,0 V", ok: false, why: "3,0 V é o limite mínimo de descarga." },
      ],
    },
    {
      stem: "Uma bateria de 2000 mAh fornece 1 A durante:",
      opts: [
        { t: "2 horas", ok: true, why: "2000 mAh = 2 Ah; 2 Ah ÷ 1 A = 2 h." },
        { t: "30 minutos", ok: false, why: "Meia hora seria com um consumo de 4 A." },
        { t: "20 horas", ok: false, why: "20 h seria com 100 mA de consumo." },
        { t: "2000 horas", ok: false, why: "Confundiu mAh com horas diretas." },
      ],
    },
    {
      stem: "Bateria 3000 mAh 20C: qual a corrente máxima?",
      opts: [
        { t: "60 A", ok: true, why: "20C × 3 Ah = 60 A." },
        { t: "20 A", ok: false, why: "Usou só o C, sem multiplicar pela capacidade." },
        { t: "3 A", ok: false, why: "3 A é a capacidade em Ah, não o máximo." },
        { t: "600 A", ok: false, why: "Errou a ordem de grandeza por 10×." },
      ],
    },
    {
      stem: "Descarregar uma célula de lítio abaixo de 3,0 V:",
      opts: [
        { t: "Danifica a célula, às vezes de forma permanente", ok: true, why: "Por isso 18650 com BMS são mais seguras para iniciantes." },
        { t: "É recomendado para calibrar", ok: false, why: "Não existe calibração por descarga profunda em lítio." },
        { t: "Aumenta a capacidade", ok: false, why: "Só degrada a química da célula." },
        { t: "Não tem efeito nenhum", ok: false, why: "Tem — e é um dos erros mais caros com LiPo." },
      ],
    },
    {
      stem: "A placa BMS em baterias 18650 serve para:",
      opts: [
        { t: "Proteger contra sobrecarga, descarga profunda e curto", ok: true, why: "É a proteção que a LiPo 'nua' não tem." },
        { t: "Aumentar a tensão de saída", ok: false, why: "BMS protege; não é um conversor elevador." },
        { t: "Equilibrar o peso do robô", ok: false, why: "BMS = Battery Management System, nada de mecânica." },
        { t: "Medir a temperatura do motor", ok: false, why: "A BMS cuida da bateria, não do motor." },
      ],
    },
    {
      stem: "Para alimentar o Arduino com uma bateria de 7,4 V:",
      opts: [
        { t: "Usar o pino VIN (passa pelo regulador interno) ou um BEC de 5 V", ok: true, why: "E sempre com GND comum ao resto do circuito." },
        { t: "Ligar direto no pino 5V", ok: false, why: "O pino 5V não passa pelo regulador: 7,4 V ali danifica a placa." },
        { t: "Ligar em um pino digital", ok: false, why: "Pinos digitais são sinais, não entrada de energia." },
        { t: "Não é possível usar 7,4 V", ok: false, why: "É possível e comum — pelo caminho certo." },
      ],
    },
    {
      stem: "Por que a porta USB não serve para alimentar motores?",
      opts: [
        { t: "A corrente disponível é baixa demais para os picos do motor", ok: true, why: "O arranque de um motor derruba a porta USB." },
        { t: "A tensão é alta demais", ok: false, why: "5 V não é o problema; a corrente é." },
        { t: "USB só transmite dados", ok: false, why: "USB alimenta, mas com corrente limitada." },
        { t: "O conector esquenta e derrete", ok: false, why: "Antes disso a tensão já cai e tudo reseta." },
      ],
    },
    {
      stem: "Na fórmula do torque, dobrar a massa do robô:",
      opts: [
        { t: "Dobra o torque necessário por motor", ok: true, why: "A massa entra multiplicando: relação linear." },
        { t: "Quadruplica o torque necessário", ok: false, why: "Seria o caso se a massa entrasse ao quadrado — não entra." },
        { t: "Não muda o torque", ok: false, why: "Massa é o primeiro fator da fórmula." },
        { t: "Reduz o torque pela metade", ok: false, why: "Mais peso nunca pede menos força." },
      ],
    },
    {
      stem: "Trocar rodas de 65 mm por rodas de 130 mm (o dobro do raio):",
      opts: [
        { t: "Dobra o torque necessário e dobra a velocidade linear na mesma RPM", ok: true, why: "O raio entra na fórmula do torque e no perímetro da roda." },
        { t: "Reduz o torque necessário", ok: false, why: "Braço de alavanca maior exige mais torque." },
        { t: "Não muda nada", ok: false, why: "Muda torque e velocidade ao mesmo tempo." },
        { t: "Só muda a estética", ok: false, why: "É uma decisão de engenharia, não de aparência." },
      ],
    },
    {
      stem: "Roda de 65 mm (perímetro ≈ 0,204 m) a 200 RPM anda a cerca de:",
      opts: [
        { t: "0,68 m/s", ok: true, why: "0,204 × 200 ÷ 60 ≈ 0,68 m/s." },
        { t: "6,8 m/s", ok: false, why: "Errou por 10× — esqueceu de dividir direito pelos 60 s." },
        { t: "0,068 m/s", ok: false, why: "Errou por 10× para baixo." },
        { t: "200 m/s", ok: false, why: "Confundiu RPM com velocidade linear." },
      ],
    },
    {
      stem: "Qual é a ordem certa ao montar o primeiro protótipo?",
      opts: [
        { t: "Testar o motor solto na bancada com um código simples, depois integrar chassi e sensores", ok: true, why: "É o item 5 do checklist: isolar problemas antes de integrar." },
        { t: "Montar tudo de uma vez e testar no final", ok: false, why: "Qualquer falha vira um mistério com dezenas de suspeitos." },
        { t: "Começar pelos sensores", ok: false, why: "Sensores vêm por último, depois da locomoção funcionar." },
        { t: "Programar todo o código antes de ligar qualquer coisa", ok: false, why: "Sem testar o hardware cedo, o código se baseia em suposições." },
      ],
    },
  ],
};
